import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../../i18n';
import { subscribeToNewsletter } from '../../api/client';

interface EmailSubscriptionPopupProps {
  onSubscribe?: (email: string) => void;
}

const SUBSCRIPTION_SHOWN_KEY = 'svet-nekretnina-subscription-shown';
const SUBSCRIPTION_EMAIL_KEY = 'svet-nekretnina-subscribed-email';

export function EmailSubscriptionPopup({ onSubscribe }: EmailSubscriptionPopupProps) {
  const { t } = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error' | 'already'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Check if popup was already shown in this session
    const wasShown = sessionStorage.getItem(SUBSCRIPTION_SHOWN_KEY);
    const subscribedEmail = localStorage.getItem(SUBSCRIPTION_EMAIL_KEY);

    if (wasShown || subscribedEmail) {
      return;
    }

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercent >= 50 && !isVisible) {
        setIsVisible(true);
        sessionStorage.setItem(SUBSCRIPTION_SHOWN_KEY, 'true');
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      setStatus('error');
      setErrorMessage(t.subscription.invalidEmail);
      return;
    }

    const subscribedEmail = localStorage.getItem(SUBSCRIPTION_EMAIL_KEY);
    if (subscribedEmail === email) {
      setStatus('already');
      return;
    }

    try {
      // Call API to subscribe
      const response = await subscribeToNewsletter(email);
      
      if (response.success) {
        // Save subscription to localStorage
        localStorage.setItem(SUBSCRIPTION_EMAIL_KEY, email);
        setStatus('success');
        onSubscribe?.(email);
        
        // Hide popup after success
        setTimeout(() => {
          setIsVisible(false);
        }, 3000);
      } else {
        // Check if already subscribed message
        if (response.message?.toLowerCase().includes('već') || 
            response.message?.toLowerCase().includes('already')) {
          localStorage.setItem(SUBSCRIPTION_EMAIL_KEY, email);
          setStatus('already');
          setTimeout(() => {
            setIsVisible(false);
          }, 2000);
        } else {
          setStatus('error');
          setErrorMessage(response.error || t.errors.generic);
        }
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setErrorMessage(t.errors.generic);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(SUBSCRIPTION_SHOWN_KEY, 'true');
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleDismiss}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
          >
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto pointer-events-auto relative">
              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Icon */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-4">
                  <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {t.subscription.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {t.subscription.subtitle}
                </p>
              </div>

              {status === 'success' ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full mb-3">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-green-600 dark:text-green-400 font-medium">
                    {t.subscription.success}
                  </p>
                </div>
              ) : status === 'already' ? (
                <div className="text-center py-4">
                  <p className="text-amber-600 dark:text-amber-400">
                    {t.subscription.alreadySubscribed}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-4">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setStatus('idle');
                      }}
                      placeholder={t.subscription.emailPlaceholder}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    {status === 'error' && (
                      <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-amber-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    {t.subscription.subscribe}
                  </button>

                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="w-full text-slate-500 dark:text-slate-400 text-sm hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  >
                    {t.subscription.dismiss}
                  </button>

                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                    {t.subscription.privacyNote}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
